import { get, put, patch, post, del } from "helpers/api_helper"
import { cachedGet } from "helpers/requestCache"
import toastr from "toastr"
import {
  getPlayersLocationFilters,
  normalizePlayerListFilters,
  normalizePlayerListOptions,
} from "./playerListQueryPolicy"

const PLAYER_ENTRY_PAGE_LIMIT = 100
// Accounts groups every matching player for these options (seconds on DEV), so one filter set is read once per ten minutes.
const PLAYER_LOCATIONS_CACHE_TTL_MS = 10 * 60 * 1000

const getAllPlayerEventPages = async (path, options = {}) => {
  const queryOptions = {
    ...options,
    page: 1,
    limit: PLAYER_ENTRY_PAGE_LIMIT,
  }
  const toUrl = page =>
    `${path}?${new URLSearchParams({ ...queryOptions, page }).toString()}`
  const firstResponse = await get(toUrl(1))
  const firstPage = firstResponse?.data || {}
  const responsePageSize = Number(firstPage.limit) || PLAYER_ENTRY_PAGE_LIMIT
  const totalPages =
    Number(firstPage.totalPages) ||
    Math.ceil((Number(firstPage.totalResults) || 0) / responsePageSize)

  if (totalPages <= 1) return firstResponse

  const remainingResponses = await Promise.all(
    Array.from({ length: totalPages - 1 }, (value, index) =>
      get(toUrl(index + 2))
    )
  )
  const results = [firstResponse, ...remainingResponses].flatMap(
    response => response?.data?.results || []
  )

  return {
    ...firstResponse,
    data: {
      ...firstPage,
      page: 1,
      limit: results.length,
      totalPages: 1,
      results,
    },
  }
}

export const getPlayers = async (filter, options) => {
  const filters = new URLSearchParams({
    ...normalizePlayerListFilters(filter),
    ...normalizePlayerListOptions(options),
  }).toString()
  const response = await get(`/find-players/search?${filters}`, { timeout: 15000 })
  if (response?.success === false || !Array.isArray(response?.data?.results) ||
    response.data.results.some(player => !player || typeof player !== "object" || Array.isArray(player))) {
    throw new Error("PLAYER_SEARCH_UNAVAILABLE")
  }
  return response
}

export const getPlayersLocations = async filter => {
  let response
  try {
    const filters = new URLSearchParams(
      getPlayersLocationFilters(filter)
    ).toString()
    response = await cachedGet(`/accounts/locations?role=player&${filters}`, {
      ttlMs: PLAYER_LOCATIONS_CACHE_TTL_MS,
      fetcher: url => get(url),
    })
  } catch (error) {
    response = error.response.data
  }
  return response
}

export const getPlayersList = async (filter, options) => {
  let response
  try {
    const filters = new URLSearchParams({
      ...normalizePlayerListFilters(filter),
      ...normalizePlayerListOptions(options),
    }).toString()
    response = await get(`/accounts?role=player&${filters}`)
  } catch (error) {
    response = error.response.data
  }
  return response
}

export const getPlayerInfo = id => get(`/accounts/${id}`, { timeout: 15000 })

export const createPlayer = async data => {
  let response
  try {
    response = await post("/admin/accounts", data)
  } catch (error) {
    response = error.response.data
  }
  return response
}

export const getPlayerTransactions = async (data, transactionsOptions) => {
  let response
  try {
    const filters = new URLSearchParams(
      Object.assign(transactionsOptions)
    ).toString()
    response = await get(`/admin/accounts/${data}/transactions?${filters}`)
  } catch (error) {
    console.log(error)
  }
  return response
}

export const getPlayerWalletTransactions = async (data, options) => {
  let response
  try {
    const filters = new URLSearchParams(Object.assign(options)).toString()
    response = await get(
      `/admin/accounts/${data}/transactions?paymentType=virtualWallet&${filters}`
    )
  } catch (error) {
    console.log(error)
  }
  return response
}

export const getMembershipList = async (data, options) => {
  let response
  try {
    const filters = new URLSearchParams(Object.assign(options)).toString()
    response = await get(
      `/admin/accounts/${data}/transactions?type=upgradeMembership,upgradeMembershipByAdmin&${filters}`
    )
  } catch (error) {
    console.log(error)
  }
  return response
}

export const getPlayerActivity = async (data, options) => {
  let response
  try {
    const filters = new URLSearchParams(Object.assign(options)).toString()
    response = data && (await get(`logs/players?playerId=${data}&${filters}`))
  } catch (error) {
    console.log(error)
  }
  return response
}

export const getLevelChange = async (data, options) => {
  let response
  try {
    const filters = new URLSearchParams(Object.assign(options)).toString()
    response =
      data &&
      (await get(
        `logs/players?playerId=${data}&type=updateGameLevel&${filters}`
      ))
  } catch (error) {
    console.log(error)
  }
  return response
}

export const getPlayerEntriesapi = async (id, playerEntriesOptions) => {
  let response
  try {
    response =
      id &&
      (await getAllPlayerEventPages(
        `/accounts/${id}/events/joined`,
        playerEntriesOptions
      ))
  } catch (error) {
    response = error.response.data
  }
  return response
}

export const getPlayerHistoryEntriesapi = async (
  id,
  playerEntriesHistoryOptions
) => {
  let response
  try {
    response =
      id &&
      (await getAllPlayerEventPages(
        `/accounts/${id}/events/closed`,
        playerEntriesHistoryOptions
      ))
  } catch (error) {
    response = error.response.data
  }
  return response
}

export const updatePlayer = async (id, data) => {
  let response
  try {
    response = await put(`/admin/accounts/${id}`, data)
  } catch (error) {
    response = error.response.data
  }
  return response
}

export const updateStatus = async (id, status) => {
  let response
  try {
    response = await patch(`/admin/accounts/${id}/status`, { status })
  } catch (error) {
    response = error.response.data
  }
  return response
}

export const updatePlayerWallet = async (id, amount) => {
  let response
  try {
    response = await patch(`/admin/accounts/${id}/wallet-balance`, amount)
  } catch (error) {
    response = error.response.data
  }
  return response
}

export const updatePlayerMembership = async (id, membership) => {
  let response
  try {
    response = await patch(`/admin/accounts/${id}/membership`, membership)
  } catch (error) {
    response = error.response.data
  }

  return response
}

export const deletePlayer = async id => {
  let response
  try {
    response = await del(`/admin/accounts/${id}`)
  } catch (error) {
    response = error.response.data
  }
  return response
}

export const updatePlayerLevel = async (id, data) => {
  let response
  try {
    response = await patch(`/admin/accounts/${id}/game-level`, data)
  } catch (error) {
    response = error.response.data
  }
  return response
}

export const getPlayerRatings = async ({ id, page, limit }) => {
  let response
  try {
    response = await get(`/accounts/${id}/ratings?page=${page}&limit=${limit}`)
  } catch (error) {
    response = error.response.data
  }
  return response
}
